import { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMutation, useServerErrors } from '../../../Shared/api';
import { Editor } from '../../../Shared/Editor/Editor';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { User } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useRouter } from '../../router';
import { Charity, DonationForm, DonationFormMailBodies, DonationFormMailSubjects, DonationFormMailType, SubscriptionMailVariables } from '../../types';
import Layout from '../../UI/Layout';
import MailEditor from '../Projects/MailEditor';

const mailTypes = [
  'donation_confirmation',
  'subscription_confirmation',
  'subscription_update',
  'subscription_cancellation',
] as DonationFormMailType[];

export interface EditDonationFormFormProps {
  donationForm: DonationForm & DonationFormMailSubjects & DonationFormMailBodies & SubscriptionMailVariables;
  charity: Charity;
  user: User;
}

export default function EditDonationFormForm({
  donationForm,
  charity,
  user,
}: EditDonationFormFormProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      donation_confirmation_subject: donationForm.donation_confirmation_subject,
      donation_confirmation_mail: donationForm.donation_confirmation_mail,
      subscription_confirmation_subject: donationForm.subscription_confirmation_subject,
      subscription_confirmation_mail: donationForm.subscription_confirmation_mail,
      subscription_update_subject: donationForm.subscription_update_subject,
      subscription_update_mail: donationForm.subscription_update_mail,
      subscription_cancellation_subject: donationForm.subscription_cancellation_subject,
      subscription_cancellation_mail: donationForm.subscription_cancellation_mail,
    },
  });

  const {
    register, control, handleSubmit, watch,
    formState: { defaultValues, errors },
  } = form;

  const mailValues = {
    donation_confirmation: watch('donation_confirmation_mail'),
    subscription_confirmation: watch('subscription_confirmation_mail'),
    subscription_update: watch('subscription_update_mail'),
    subscription_cancellation: watch('subscription_cancellation_mail'),
  };

  const [editDonationForm, { errors: serverErrors, loading }] = useMutation(
    routes.edit_donation_form_mails(donationForm.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:donation_form_saved'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const submit = async (data: typeof defaultValues) => {
    await editDonationForm({
      donation_confirmation_subject: data?.donation_confirmation_subject,
      donation_confirmation_mail: data?.donation_confirmation_mail,
      subscription_confirmation_subject: data?.subscription_confirmation_subject,
      subscription_confirmation_mail: data?.subscription_confirmation_mail,
      subscription_update_subject: data?.subscription_update_subject,
      subscription_update_mail: data?.subscription_update_mail,
      subscription_cancellation_subject: data?.subscription_cancellation_subject,
      subscription_cancellation_mail: data?.subscription_cancellation_mail,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_mails')}
          </h3>
        </div>
        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />
          </div>
        )}
        {mailTypes.map((mailType) => (
          <div className="grid grid-cols-[1fr_2fr] gap-8" key={mailType}>
            <div>
              <h4 className="font-medium text-slate-500 mb-1">
                {t(`backend:donation_form_mails.${mailType}_mail`)}
              </h4>
              <div className="text-slate-500">
                {t(`backend:donation_form_mails.${mailType}_mail_description`)}
              </div>
            </div>
            <div className="space-y-6">
              <InputGroup>
                <InputLabel htmlFor={`${mailType}_subject`}>
                  {t('backend:subject')}
                </InputLabel>
                <Input {...register(`${mailType}_subject`)} id={`${mailType}_subject`} />
                <ErrorMessage attribute={t('backend:body')} error={errors[`${mailType}_subject`] }/>
              </InputGroup>
              <InputGroup>
                <InputLabel>
                  {t('backend:body')}
                </InputLabel>
                <MailEditor
                  type={mailType}
                  editor={(
                    <Controller
                      control={control}
                      name={`${mailType}_mail`}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Editor
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          allowProjectColor
                          variables={mailType.startsWith('subscription') ? donationForm.subscription_mail_variables : []}
                        />
                      )}
                    />
                  )}
                  preview={mailValues[mailType]}
                  donationForm={donationForm}
                  charity={charity}
                  headerUrl={charity.mail_header_url}
                  user={user}
                />
                <ErrorMessage attribute={t('backend:body')} error={errors[`${mailType}_mail`] }/>
              </InputGroup>
            </div>
          </div>
        ))}

        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

EditDonationFormForm.layout = (page: ReactNode) => <Layout>{page}</Layout>;
